<template>
  <AppFormGroup v-if="applicantType === ApplicantType.PRIMARY">
    <SubHeading keypath="headings.card" />

    <div class="flex flex-col md:flex-row md:gap-x-4">
      <AppField path="bankCard.cardholderFirstName" class="md:basis-2/4" />
      <AppField path="bankCard.cardholderLastName" class="md:basis-2/4" />
    </div>

    <div class="flex flex-col lg:flex-row lg:gap-x-4">
      <AppField path="bankCard.cardNumber" class="md:basis-3/6" />

      <div class="flex flex-col md:flex-row md:gap-x-4 lg:basis-3/6">
        <AppField
          v-model="expirationMonth"
          path="bankCard.expirationMonth"
          preferred-component="dropdown"
          class="md:basis-2/6"
          :option-disabled="expirationMonthFilter"
        />

        <AppField
          v-model="expirationYear"
          path="bankCard.expirationYear"
          preferred-component="dropdown"
          class="md:basis-2/6"
          :option-disabled="expirationYearFilter"
        />

        <AppField
          path="bankCard.cvv"
          max-length="4"
          class="md:basis-2/6"
          privacy-mode="all"
        />
      </div>
    </div>

    <DisclosureDictionary :provider-key="providerKey" type="bank-card" />

    <div class="flex flex-col md:flex-row lg:gap-x-4">
      <AppField path="bankCard.zipCode" class="md:basis-1/4" />
    </div>
  </AppFormGroup>

  <AppFormGroup v-if="applicantType === ApplicantType.PRIMARY">
    <SubHeading keypath="headings.billingAddress" class="mt-4" />
    <div class="pt-6">
      <UIInputCheckbox
        id="bankCardAddressSameAsHomeAddress"
        v-model="bankCardAddressSameAsHomeAddress"
        :label="t('bankCardAddressSameAsHomeAddress.label')"
        :required="false"
        class="-mb-6"
      />
    </div>
    <div class="flex flex-col md:flex-row md:gap-x-4">
      <AppField
        path="bankCard.address.line1"
        :disabled="bankCardAddressSameAsHomeAddress || fetchingZip"
        class="md:basis-3/4"
      />

      <AppField
        path="bankCard.address.line2"
        :disabled="bankCardAddressSameAsHomeAddress || fetchingZip"
        class="md:basis-1/4"
      />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-4">
      <AppField
        :disabled="bankCardAddressSameAsHomeAddress || fetchingZip"
        path="bankCard.address.zipCode"
        class="larger-help"
      >
        <template v-if="showZipCodeHelpText" #helpText>
          {{ t("appFields.zipCode.helpText") }}
        </template>
      </AppField>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-4">
      <AppField
        v-model:visible="cityFieldVisible"
        path="bankCard.address.city"
        :disabled="bankCardAddressSameAsHomeAddress || fetchingZip"
      />
      <AppField
        v-model:visible="stateFieldVisible"
        path="bankCard.address.state"
        :disabled="bankCardAddressSameAsHomeAddress || fetchingZip"
      />
    </div>

    <div class="flex flex-col md:flex-row md:gap-x-4">
      <AppField
        path="paymentMethod"
        preferred-component="dropdown"
        class="md:basis-1/2"
      />
    </div>
  </AppFormGroup>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue"
  import {
    AppField,
    AppFormGroup,
    useAppFieldModel,
    type AppFieldProps,
  } from "@versatile-credit-apply/components/form"

  import { DisclosureDictionary } from "@versatile-credit-apply/components"

  import { ApplicantType } from "@versatile-credit-apply/omni-apply-client"
  import {
    useApplicationStore,
    useFetchZipCodeMetadata,
    useProviderI18n,
  } from "@versatile-credit-apply/sdk"
  import type { FormSectionProps } from "./form-section-api"

  const props = defineProps<FormSectionProps>()

  const stateFieldVisible = ref<boolean>(false)
  const cityFieldVisible = ref<boolean>(false)
  const showZipCodeHelpText = computed<boolean>(
    () => stateFieldVisible.value && cityFieldVisible.value
  )

  // Not all providers collect this field, but they support billing address.
  // We'll use this model but NOT the AppField to allow this functionality
  // for those providers that do not use the `billingAddressSameAsHome` field.
  const bankCardAddressSameAsHomeAddress = useAppFieldModel(
    "bankCard.billingAddressSameAsHome"
  )

  const application = useApplicationStore()

  const homeAddress = useAppFieldModel("primary.homeAddress")
  const bankCardAddress = useAppFieldModel("bankCard.address")
  // autofill billing address city / state based on zip provided
  const fetchingZip = useFetchZipCodeMetadata(
    () => bankCardAddress.value?.zipCode,
    (metadata) => {
      if (bankCardAddressSameAsHomeAddress.value) {
        return
      }

      /** non-null assert
     if we have made it this far we are guaranteed to have a object
     we can key into, TS just cant figure it out based on how the AppField,
     key paths and Application store are composed
    */
      bankCardAddress.value!.city = metadata.city
      bankCardAddress.value!.state = metadata.state
    }
  )

  // Underlying model is a string
  const expirationMonth = ref<string>()

  // Underlying model is a string
  const expirationYear = ref<string>()

  const now = new Date()

  const expirationMonthFilter: AppFieldProps["optionDisabled"] = (option) => {
    // No year selected...all options are valid
    if (expirationYear.value == undefined) {
      return false
    }

    const year = parseInt(expirationYear.value)
    if (now.getFullYear() !== year) {
      return false
    }

    const month = parseInt(option.value)
    return now.getMonth() + 1 > month
  }

  const expirationYearFilter: AppFieldProps["optionDisabled"] = (option) => {
    // No month selected...all options are valid
    if (expirationMonth.value == undefined) {
      return false
    }

    const year = parseInt(option.value)
    if (year < now.getFullYear()) {
      return true
    }

    if (year !== now.getFullYear()) {
      return false
    }

    const month = parseInt(expirationMonth.value)
    return month < now.getMonth() + 1
  }

  watch(bankCardAddressSameAsHomeAddress, (sameAsHomeAddress) => {
    if (sameAsHomeAddress && homeAddress.value) {
      bankCardAddress.value = {
        ...homeAddress.value,
      }
    } else {
      bankCardAddress.value = undefined
    }
  })

  onMounted(() => {
    bankCardAddressSameAsHomeAddress.value = true

    // We need to manually update the ref used as the v-model for
    // our fields. There is a dirty check issue that was fixed in
    // Vue 3.5 but we can't upgrade to that yet.
    const bankCard = application.application?.bankCard
    if (bankCard?.expirationYear != undefined) {
      expirationYear.value = String(bankCard.expirationYear)
    }

    if (bankCard?.expirationMonth != undefined) {
      expirationMonth.value = String(bankCard.expirationMonth)
    }
  })

  const { t } = useProviderI18n(() => props.providerKey)
</script>
<i18n lang="yaml" locale="en">
appFields:
  bankCardZipCode:
    label: Billing Postal Code
  cardNumber:
    label: Card Number
  cardholderFirstName:
    label: Cardholder First Name
  cardholderLastName:
    label: Cardholder Last Name
  cvv:
    label: CVV
  expirationMonth:
    label: Exp. Month
  expirationYear:
    label: Exp. Year
  paymentMethod:
    label: Payment Method
  zipCode:
    helpText: Enter ZIP Code to see City and State
bankCardAddressSameAsHomeAddress:
  label: Same as Home Address
headings:
  billingAddress: Billing Address
  card: Card Information
</i18n>

<i18n lang="yaml" locale="aff_standard_en">
appFields:
  cardNumber:
    label: Debit Card Number
headings:
  card: Debit Card Information
</i18n>

<i18n lang="yaml" locale="easypay_standard_en">
appFields:
  paymentMethod:
    description: You (the customer) can change your preferred payment method now or at any time.
    label: My Payments will be made by
</i18n>
<style scoped>
  @reference "@/main.css";

  .larger-help :deep(small:not(.error-text)) {
    @apply text-base;
  }
</style>
